import React, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { TextFieldProps } from "common/components/TextArea/TextArea.types";
import { isNil } from "common/components/TextArea/utils/isNil";
import { FormField } from './ui/FormField/FormField';

export const NormalizedTextArea = styled.textarea`
  font-family: inherit;
  font-size: 100%;
  line-height: 1.15;
  margin: 0;
  overflow: visible;
`;

const StyledTextArea = styled(NormalizedTextArea)`
  border: none;
  outline: none;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #757575;
  background-color: #fff;
  width: 100%;
  &:disabled {
    background-color: #fafafa;
  }
  &:focus {
    color: #212121;
  }
`;

export const TextArea = forwardRef<HTMLTextAreaElement, TextFieldProps>(
  (
    {
      type = 'text',
      prefix,
      suffix,
      leadingIcon,
      trailingIcon,
      error,
      warning,
      success,
      hint,
      label,
      disabled = false,
      required = false,
      name,
      id: idProp,
      fullWidth = false,
      value: valueProp,
      hideLabel = false,
      onChange,
      className,
      rows,
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState(valueProp ?? '');
    const onChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
      if (onChange) {
        onChange(event);
      }
    };
    useEffect(() => {
      if (!isNil(valueProp)) {
        setValue(valueProp);
      }
    }, [valueProp, setValue]);

    const id = idProp || `textarea-${name}`;

    return (
      <FormField
        inputId={id}
        fullWidth={fullWidth}
        hideLabel={hideLabel}
        prefix={prefix}
        suffix={suffix}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        error={error}
        warning={warning}
        success={success}
        hint={hint}
        label={label}
        required={required}
        disabled={disabled}
        className={className}>
        <StyledTextArea
          id={id}
          rows={rows}
          name={name}
          disabled={disabled}
          required={required}
          type={type}
          ref={ref}
          value={value}
          onChange={onChangeHandler}
          {...props}
        />
      </FormField>
    );
  },
);
